import { t } from '@/features/i18n/utils';
import { useAtom, useAtomValue } from 'jotai';
import { UnreadIndicator } from '@/features/comments/components/unread-indicator';
import { LATEST_RELEASE_NOTE_DATE } from '@/features/release-notes/constants';
import { releaseNotesSeenDateAtom } from '@/features/release-notes/store';
import { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionItem, AccordionPanel } from '@/ui/components/accordion';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '@/ui/components/collapsible';
import {
  sidebarClosedSectionsAtom,
  sidebarStateAtom,
  sidebarWidthAtom,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
} from '@/ui/components/sidebar/store';
import { cn } from '@/ui/utils';
import { Menu, MenuItem, MenuPopup, MenuTrigger, MenuTriggerIcon } from '@/ui/components/menu';
import { useAuth } from '@/features/auth';
import { OrganizationAvatar } from '@/features/organizations/components/organization-avatar';
import { SwitchOrganizationMenu } from '@/features/organizations/components/switch-organization-menu';
import {
  ArrowRight01Icon,
  ArrowRightIcon,
  CustomerSupportIcon,
  HelpCircleIcon,
  Logout01Icon,
  MessageMultiple01Icon,
  NewsIcon,
  PlusSignIcon,
  SettingsIcon,
  SidebarRightIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { Button } from '@/ui/components/button';
import { ScrollArea } from '@/ui/components/scroll-area';
import { Dialog, DialogPopup } from '@/ui/components/dialog';
import { UserAvatar } from '@/ui/components/user-avatar';
import { Logo } from '@/ui/components/logo';
import { Link } from '@tanstack/react-router';
import { LinkProps } from '@tanstack/react-router';
import { Skeleton } from '@/ui/components/skeleton';
import { HoverScrollText } from '@/ui/components/hover-scroll-text';

export function Sidebar({ className, children, ...props }: React.ComponentProps<'div'>) {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [state, setState] = useAtom(sidebarStateAtom);
  const [width, setWidth] = useAtom(sidebarWidthAtom);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);
  const hoverTriggerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShouldAnimate(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + delta;

      if (newWidth < SIDEBAR_MIN_WIDTH - 150) {
        isResizingRef.current = false;
        setIsResizing(false);
        setState('collapsed');
        document.body.style.userSelect = '';
        delete document.documentElement.dataset.resizing;
        return;
      }

      setWidth(Math.max(SIDEBAR_MIN_WIDTH, Math.min(newWidth, SIDEBAR_MAX_WIDTH)));
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = '';
      delete document.documentElement.dataset.resizing;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <>
      <div
        data-slot="sidebar"
        data-should-animate={shouldAnimate ? '' : undefined}
        data-expanded={state === 'expanded' ? '' : undefined}
        data-collapsed={state === 'collapsed' || state === 'floating' ? '' : undefined}
        className={cn(
          'relative shrink-0 overflow-hidden max-md:hidden h-svh flex flex-col transform-gpu data-should-animate:transition-[width] transition-none duration-300 ease-smooth data-collapsed:w-0',
          className,
        )}
        style={{
          width: state === 'expanded' ? width : 0,
          transition: isResizing ? 'none' : undefined,
        }}
        {...props}
      >
        {children}
      </div>
      {state === 'expanded' && (
        <div
          style={{ left: width }}
          onMouseDown={(e) => {
            startXRef.current = e.clientX;
            startWidthRef.current = width;
            isResizingRef.current = true;
            setIsResizing(true);
            document.body.style.userSelect = 'none';
            document.documentElement.dataset.resizing = '';
          }}
          data-resizing={isResizing ? '' : undefined}
          className="absolute opacity-0 max-md:hidden translate-x-0.5 flex justify-center transition-opacity duration-300 data-resizing:opacity-100 hover:opacity-100 top-0 bottom-0 w-3 h-[calc(100%-var(--main-margin)-1rem)] my-auto overflow-hidden cursor-ew-resize z-2"
        >
          <div className="w-0.5 bg-surface-9 rounded-md h-full"></div>
        </div>
      )}
      {(state === 'collapsed' || state === 'floating') && (
        <>
          <div
            ref={hoverTriggerRef}
            onMouseOver={() => {
              if (state === 'collapsed') setState('floating');
            }}
            className="fixed left-0 top-0 bottom-0 w-(--main-margin) z-40 max-md:hidden"
            aria-hidden="true"
          />
          <Dialog
            open={state === 'floating'}
            onOpenChange={(open) => setState(open ? 'floating' : 'collapsed')}
          >
            <DialogPopup
              onMouseOut={(e) => {
                const sidebarEl = sidebarRef.current;
                if (state !== 'floating' || !sidebarEl) return;
                const rect = sidebarEl.getBoundingClientRect();

                if (e.clientX > rect.right || e.clientX < rect.left - 8) {
                  setState('collapsed');
                }
              }}
              ref={sidebarRef}
              variant="sidebar"
              className="flex flex-col pr-2"
            >
              {children}
            </DialogPopup>
          </Dialog>
        </>
      )}
    </>
  );
}

export function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  const auth = useAuth();
  const [width] = useAtom(sidebarWidthAtom);
  if (!auth.user.organizationId) return null;

  return (
    <ScrollArea
      scrollbar={false}
      data-slot="sidebar-content"
      viewportClassName={cn('p-3 pr-1 grow space-y-4 flex flex-col', className)}
      style={{ width }}
      {...props}
    />
  );
}

export function SidebarTrigger({
  className,
  ...props
}: React.ComponentProps<typeof SidebarHeaderButton>) {
  const [state, setState] = useAtom(sidebarStateAtom);

  return (
    <SidebarHeaderButton
      data-state={state}
      data-floating={state === 'floating' ? '' : undefined}
      className={cn(
        'data-floating:opacity-0 data-floating:delay-0 data-floating:w-0 data-floating:pointer-events-none data-state-collapsed:delay-450',
        className,
      )}
      onClick={() => {
        setState((prev) => {
          if (prev === 'expanded') return 'collapsed';
          return 'expanded';
        });
      }}
      {...props}
    >
      <Icon icon={SidebarRightIcon} />
    </SidebarHeaderButton>
  );
}

export function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      className={cn('flex justify-between mb-3', className)}
      {...props}
    />
  );
}

export function SidebarHeaderButton(props: React.ComponentProps<typeof Button>) {
  return <Button data-slot="sidebar-header-button" variant="ghost" kind="icon" {...props} />;
}

interface SidebarSectionProps extends React.ComponentProps<typeof Collapsible> {
  id: string;
}

export function SidebarSection({ id, ...props }: SidebarSectionProps) {
  const [closedSections, setClosedSections] = useAtom(sidebarClosedSectionsAtom);

  return (
    <Collapsible
      data-slot="sidebar-section"
      open={!closedSections.includes(id)}
      onOpenChange={(open) =>
        setClosedSections((prev) => (open ? prev.filter((s) => s !== id) : [...prev, id]))
      }
      {...props}
    />
  );
}

export function SidebarHeading({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleTrigger>) {
  return (
    <CollapsibleTrigger
      data-slot="sidebar-heading"
      className={cn('text-xs mb-2 gap-1 text-muted hover:text-foreground/90 pl-2', className)}
      {...props}
    >
      {children}
      <Icon
        icon={ArrowRight01Icon}
        className="size-3.5 opacity-0 mt-px transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 group-not-data-panel-open:opacity-100 group-data-panel-open:rotate-90"
      />
    </CollapsibleTrigger>
  );
}

export function SidebarSectionPanel(props: React.ComponentProps<typeof CollapsiblePanel>) {
  return <CollapsiblePanel data-slot="sidebar-section-panel" {...props} />;
}

export function SidebarList({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-slot="sidebar-list" className={cn('space-y-1', className)} {...props} />;
}

export function SidebarListEmpty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-list-empty"
      className={cn(
        'text-muted/75 border text-balance text-center border-dashed rounded-md text-xs flex items-center gap-2 flex-col py-3 px-2',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarListEmptyIcon({ className, ...props }: React.ComponentProps<typeof Icon>) {
  return (
    <Icon
      data-slot="sidebar-list-empty-icon"
      className={cn('mx-auto size-5', className)}
      {...props}
    />
  );
}

export function SidebarItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="sidebar-item" className={className} {...props} />;
}

interface SidebarLinkProps extends Omit<LinkProps, 'onClick'> {
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

export function SidebarLink({ to, ...props }: SidebarLinkProps) {
  return (
    <Button
      variant="sidebar-item"
      nativeButton={false}
      render={<Link to={to} data-slot="sidebar-link" {...props} />}
    />
  );
}

export function SidebarItemSkeleton() {
  return (
    <SidebarItem>
      <Skeleton className="h-8 rounded-md" />
    </SidebarItem>
  );
}

interface SidebarButtonProps extends React.ComponentProps<'button'> {
  className?: string;
}

export function SidebarButton(props: SidebarButtonProps) {
  return <Button data-slot="sidebar-button" variant="sidebar-item" {...props} />;
}

export function SidebarLinkIconButton({ className, ...props }: React.ComponentProps<'button'>) {
  return (
    <Button
      variant="ghost"
      kind="icon"
      size="xs"
      data-slot="sidebar-link-icon-button"
      className={cn(
        'hover:bg-surface-5 ml-auto -mr-1 group-hover/sidebar-link:visible rounded-sm invisible',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarHelpButton({ className }: { className?: string }) {
  return (
    <Menu>
      <SidebarItem>
        <MenuTrigger
          data-slot="sidebar-help-button"
          render={<Button variant="sidebar-item" />}
          className={cn(className)}
        >
          <Icon icon={CustomerSupportIcon} />
          {t({ en: 'Help and support', nb: 'Hjelp og støtte' })}
        </MenuTrigger>
      </SidebarItem>
      <MenuPopup className={'w-(--anchor-width)'} side="top">
        <MenuItem onClick={() => showIntercom()}>
          <Icon icon={MessageMultiple01Icon} />
          {t({ en: 'Support chat', nb: 'Støttechat' })}
        </MenuItem>
        <MenuItem
          render={
            <a href="https://intercom.help/Volvetech/en/" target="_blank" rel="noopener noreferrer">
              <Icon icon={HelpCircleIcon} />
              {t({ en: 'Help centre', nb: 'Hjelpesenter' })}
            </a>
          }
        />
      </MenuPopup>
    </Menu>
  );
}

export function SidebarReleaseNotes() {
  const seenDate = useAtomValue(releaseNotesSeenDateAtom);

  return (
    <SidebarItem>
      <SidebarLink to="/release-notes" className="relative">
        <Icon icon={NewsIcon} />
        {seenDate < LATEST_RELEASE_NOTE_DATE && (
          <UnreadIndicator className="absolute top-1 left-4.5" />
        )}
        {t({ en: 'Release notes', nb: 'Releasenotater' })}
      </SidebarLink>
    </SidebarItem>
  );
}

export function SidebarUserMenu({ className, ...props }: React.ComponentProps<typeof MenuTrigger>) {
  const auth = useAuth();

  return (
    <div>
      <Menu>
        <MenuTrigger
          data-slot="sidebar-user-menu"
          className={cn('flex text-left gap-2 rounded-lg h-10 pl-1! w-full', className)}
          render={<Button variant="ghost" />}
          {...props}
        >
          <div className="relative shrink-0">
            <OrganizationAvatar
              organization={{
                id: auth.user.organizationId,
                name: auth.user.organizationName ?? '',
              }}
              size={32}
              className="rounded-[calc(var(--radius-lg)-0.125rem)]"
            />
            <UserAvatar
              size={14}
              user={{ ...auth.user, name: `${auth.user.firstName} ${auth.user.lastName}` }}
              className="absolute -bottom-0.5 -right-0.5 ring-1 ring-background"
            />
          </div>
          <div className="min-w-0">
            <HoverScrollText className="font-semibold leading-tight text-sm mt-px">
              {auth.user.firstName} {auth.user.lastName}
            </HoverScrollText>
            <HoverScrollText className="text-muted text-xs">
              {auth.user.organizationName}
            </HoverScrollText>
          </div>
          <MenuTriggerIcon />
        </MenuTrigger>
        <MenuPopup className={'w-(--anchor-width)'}>
          <SwitchOrganizationMenu />
          <MenuItem render={<Link to="/settings/profile" />} nativeButton={false}>
            <Icon icon={SettingsIcon} />
            {t({ en: 'Settings', nb: 'Innstillinger' })}
          </MenuItem>
          <MenuItem variant="destructive" onClick={auth.logout}>
            <Icon icon={Logout01Icon} />
            {t({ en: 'Logout', nb: 'Logg ut' })}
          </MenuItem>
        </MenuPopup>
      </Menu>
    </div>
  );
}

export function SidebarLogo() {
  return (
    <Button
      nativeButton={false}
      render={<Link to="/" />}
      variant="ghost"
      className={
        'flex not-data-inactive:aria-[current=page]:bg-transparent hover:bg-surface-3! active:bg-surface-4! pl-2! w-full justify-start'
      }
    >
      <Logo className="size-auto w-13" />
    </Button>
  );
}

export function SidebarAccordion(props: React.ComponentProps<typeof Accordion>) {
  return <Accordion data-slot="sidebar-accordion" {...props} />;
}

export function SidebarAccordionTriggerIcon({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div className={cn('relative grid place-items-center shrink-0 size-4', className)} {...props}>
      <span className="place-items-center absolute">{children}</span>
      <span className="hidden group-hover/accordion-trigger:grid bg-surface-5 group-hover/accordion-trigger:bg-surface-5 transition-colors size-4.5 rounded-sm -top-px -left-px z-2 place-items-center absolute">
        <Icon
          icon={ArrowRightIcon}
          className="size-3.5 group-data-panel-open/accordion-trigger:rotate-90 transition-transform"
        />
      </span>
    </div>
  );
}

export function SidebarAccordionItem(props: React.ComponentProps<typeof AccordionItem>) {
  return <AccordionItem data-slot="sidebar-accordion-item" {...props} />;
}

export function SidebarAccordionPanel({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPanel>) {
  return (
    <AccordionPanel
      data-slot="sidebar-accordion-panel"
      className={cn('ml-3.75', className)}
      {...props}
    >
      <div className="border-l pl-2 mt-1">{children}</div>
    </AccordionPanel>
  );
}

export function SidebarAccordionCreateButton({
  className: _className,
  children,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      nativeButton={false}
      render={<span />}
      variant="tertiary"
      size="xs"
      className="group-hover/accordion-trigger:bg-surface-5 min-w-4.5 shrink-0 hover:bg-surface-6 active:bg-surface-7 ml-auto -mr-0.5 rounded-sm h-5 group-hover/accordion-trigger:h-4.5 px-0"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...props}
    >
      <span className="group-hover/accordion-trigger:hidden px-1">{children}</span>
      <span className="hidden group-hover/accordion-trigger:block">
        <Icon icon={PlusSignIcon} className="size-3.5" />
      </span>
    </Button>
  );
}

export function SidebarLinkCreateButton({
  className,
  children,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  if (!onClick)
    return (
      <span
        title={props.title}
        aria-label={props['aria-label']}
        className={cn(
          'ml-auto -mr-0.5 inline-flex h-5 min-w-4.5 shrink-0 items-center justify-center rounded-sm bg-surface-3 px-1 text-xs tabular-nums group-aria-[current=page]/sidebar-link:bg-surface-5 group-hover/sidebar-link:h-4.5 group-hover/sidebar-link:bg-surface-5',
          className,
        )}
      >
        {children}
      </span>
    );
  return (
    <Button
      nativeButton={false}
      render={<span />}
      variant="tertiary"
      size="xs"
      className={cn(
        'group/sidebar-count group-aria-[current=page]/sidebar-link:bg-surface-5 group-hover/sidebar-link:bg-surface-5 group-hover/sidebar-link:h-4.5 min-w-4.5 shrink-0 hover:bg-surface-6 active:bg-surface-7 ml-auto -mr-0.5 rounded-sm h-5 px-0',
        className,
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      {...props}
    >
      <span className="group-hover/sidebar-count:hidden px-1 tabular-nums">{children}</span>
      <span className="hidden group-hover/sidebar-count:block">
        <Icon icon={PlusSignIcon} className="size-3.5" />
      </span>
    </Button>
  );
}

export function SidebarIndentedPanel({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="sidebar-indented-panel" className={cn('pl-2 ml-3', className)} {...props} />
  );
}
